import { useMutation } from '@apollo/client';
import { formatPhone, isPhoneNumber, sanitizeFullName } from '@bybeam/formatting';
import {
  ApplicantTypeRole,
  MutationResponse,
  TransactionErrorDisplay,
  UpdateUserProfileInput,
  User,
} from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Checkbox from '@platform-ui-common/components/Checkbox/index.js';
import PartnerContact from '@platform-ui-common/components/ContactInfo/index.js';
import { DEFAULT_ROLE_LABELS } from '@platform-ui-common/components/CreateAccount/AccountPartySelection.js';
import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Link from '@platform-ui-common/components/Link/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Modal from '@platform-ui-common/components/Modal/index.js';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/actions.js';
import {
  checkboxField,
  emailField,
  fullNameField,
  optionalField,
  phoneField,
  requiredField,
} from '@platform-ui-common/hooks/useForm/fields.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/types.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import {
  getDefaultApplicantTypeId,
  getSortedApplicantTypes,
  hasMultiParty,
} from '@platform-ui-common/utilities/applicantType.js';
import { useEffect, useMemo, useState } from 'react';
import UpdateUserProfile from '../../graphql/UpdateUserProfileMutation.graphql';

const UserDataModal = ({
  onClose = () => {},
  onSubmit,
  user = {},
}: {
  onSubmit: () => void;
  onClose?: () => void;
  user?: Partial<User>;
}) => {
  const partner = usePartner();
  const isMultiParty = hasMultiParty(partner);
  const { name: userName, phone: userPhone, email: userEmail } = user;
  const { showSnackbar } = useSnackbar();
  const [updateProfile, { loading }] = useMutation<
    { user: { updateProfile: MutationResponse<User> } },
    { input: UpdateUserProfileInput }
  >(UpdateUserProfile);
  const [showPhoneConsent, setShowPhoneConsent] = useState(false);

  const thirdParties = useMemo(() => {
    return getSortedApplicantTypes(partner?.applicantTypes, ApplicantTypeRole.ThirdParty);
  }, [partner]);

  const {
    dispatch,
    formData,
    errors,
    trySubmit,
    isPristine,
    counters: { missingRequiredFields },
  } = useForm(
    {
      fullName: userName !== userEmail ? userName : '',
      email: userEmail,
      phone: userPhone ? formatPhone(userPhone) : '',
      userAgreement: false,
      applicantTypeId: getDefaultApplicantTypeId(
        partner?.applicantTypes,
        ApplicantTypeRole.FirstParty,
      ),
      applicantTypeRole: ApplicantTypeRole.FirstParty,
    },
    {
      fullName: fullNameField(),
      email: emailField(),
      phone: phoneField(false),
      userAgreement: checkboxField(
        'userAgreement',
        'Please agree to our Terms of Service and Privacy Policy',
      ),
      applicantTypeId: isMultiParty ? requiredField() : optionalField(),
      applicantTypeRole: isMultiParty ? requiredField() : optionalField(),
    },
    ValidationMode.OnSubmit,
  );

  const { fullName, email, phone, applicantTypeId, applicantTypeRole } = formData;

  useEffect(() => {
    setShowPhoneConsent(partner?.communicationChannels?.config?.channels?.sms && !!phone?.length);
  }, [phone, partner?.communicationChannels?.config?.channels?.sms]);

  const isThirdParty = applicantTypeRole === ApplicantTypeRole.ThirdParty;

  const roleOptions = useMemo(() => {
    const config = partner?.config?.applicantTypeRoles ?? {};
    return Object.values(ApplicantTypeRole).map((value) => ({
      label: config[value]?.description ?? DEFAULT_ROLE_LABELS[value],
      value: value,
    }));
  }, [partner?.config?.applicantTypeRoles]);

  const doMutation = async (): Promise<void> => {
    if (!user.id) return;
    if (isPristine) return onClose();
    const {
      data: {
        user: {
          updateProfile: { metadata: updateUserMetadata },
        },
      },
    } = await updateProfile({
      variables: {
        input: {
          name: sanitizeFullName(fullName),
          email,
          applicantTypeId,
          ...(phone && { phone: formatPhone(phone, 'E164') }),
        },
      },
    });

    if (updateUserMetadata.status < 400) {
      const barText =
        email !== user.email
          ? `Changes saved! A verification email was sent to ${email}. This email address will display on this page when the user completes email verification.`
          : 'User successfully updated.';
      showSnackbar(barText);
      onSubmit();
      return onClose();
    }

    let error = 'Something went wrong';
    if (updateUserMetadata.status === 400 && updateUserMetadata?.errors)
      error = TransactionErrorDisplay[updateUserMetadata.errors[0]] || updateUserMetadata.errors[0];

    showSnackbar(
      error,
      <Button variant="text" textColor={Color.Link} onClick={(): Promise<void> => doMutation()}>
        Try Again
      </Button>,
    );
  };

  return (
    <Modal
      title="Please confirm the following information:"
      isOpen
      onClickClose={onClose}
      size="M"
      preventClose
    >
      <FormContainer
        onSubmit={() => trySubmit(doMutation)}
        buttons={
          <div className="flex flex-col gap-6 items-center">
            <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
            <Button type="submit" disabled={loading}>
              Continue
            </Button>
          </div>
        }
      >
        <TextInput
          id="fullName"
          label="Full name"
          onChange={(value): void => dispatch(fieldUpdate('fullName', value))}
          value={fullName}
          error={errors.fullName as string}
          type="text"
          required
        />
        {isThirdParty && thirdParties?.length && (
          <Dropdown
            id="create-account-applicantTypeId"
            label="Account Type"
            items={thirdParties.map((applicantType) => ({
              label: applicantType.name,
              value: applicantType.id,
            }))}
            value={applicantTypeId}
            onChange={(value: string): void => dispatch(fieldUpdate('applicantTypeId', value))}
            error={!!errors.applicantTypeId}
            helperText={errors.applicantTypeId as string}
            required
            searchable={false}
            disabled={thirdParties.length === 1}
          />
        )}
        <TextInput
          id="email"
          label="Email"
          onChange={(value): void => dispatch(fieldUpdate('email', value))}
          value={email}
          error={errors.email as string}
          type="email"
          required
        />
        <TextInput
          id="phone"
          label="Phone number"
          onChange={(value): void => {
            const formatted = isPhoneNumber(value) ? formatPhone(value) : value;
            dispatch(fieldUpdate('phone', formatted));
          }}
          value={phone}
          error={errors.phone as string}
          type="tel"
        />
        {showPhoneConsent ? (
          <div className="max-w-field">
            <Message variant="info">
              By adding your phone number, you consent to receive automated text messages related to
              your application from {partner.name}. Message frequency varies. Reply{' '}
              <strong>STOP</strong> to opt out or <strong>HELP</strong> for help. Message & data
              rates apply. You can manage your preferences at any time.
            </Message>
          </div>
        ) : null}
        <div className="max-w-field">
          <Checkbox
            required
            checked={formData.userAgreement}
            onClick={(): void => dispatch(fieldUpdate('userAgreement', !formData.userAgreement))}
            label={
              <Typography variant="body" tag="span">
                By checking this box you agree to our{' '}
                <Link
                  external
                  to="https://www.bybeam.co/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  external
                  to="https://www.bybeam.co/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy.
                </Link>
              </Typography>
            }
            error={errors.userAgreement as string}
          />
        </div>
        {isMultiParty && (
          <>
            <Typography variant="body">Which statement best describes you?*</Typography>
            <div className="flex flex-col items-center w-field gap-y-4 lg:self-center lg:max-w-[20rem]">
              <RadioGroup
                name="applicantTypeId"
                selectedValue={applicantTypeRole}
                onChange={(value: ApplicantTypeRole): void => {
                  dispatch(fieldUpdate('applicantTypeRole', value));
                  dispatch(
                    fieldUpdate(
                      'applicantTypeId',
                      getDefaultApplicantTypeId(partner?.applicantTypes, value),
                    ),
                  );
                }}
                items={roleOptions}
                error={!!errors.applicantTypeId}
                helperText={errors.applicantTypeId as string}
              />
              <Message variant="info">
                Need help choosing the right statement? Please contact us at{' '}
                <PartnerContact email={partner?.email} phone={partner?.phone} />.
              </Message>
            </div>
          </>
        )}
      </FormContainer>
    </Modal>
  );
};

export default UserDataModal;
